import { useCallback, useRef } from 'react'
import { ConnectionQuality } from 'livekit-client'
import { toast } from 'sonner'
import { toQualityLevel } from '@/types/network'

interface UseNetworkToastsOptions {
  /** Debounce time to prevent toast spam during rapid quality changes */
  debounceMs?: number
}

export function useNetworkToasts(options: UseNetworkToastsOptions = {}) {
  const { debounceMs = 3000 } = options
  const lastToastTimeRef = useRef<Record<string, number>>({})

  const shouldShowToast = useCallback(
    (key: string) => {
      const now = Date.now()
      const lastTime = lastToastTimeRef.current[key] ?? 0
      if (now - lastTime < debounceMs) {
        return false
      }
      lastToastTimeRef.current[key] = now
      return true
    },
    [debounceMs]
  )

  const handleLocalQualityChanged = useCallback(
    (quality: ConnectionQuality, previous: ConnectionQuality) => {
      const newLevel = toQualityLevel(quality)
      const prevLevel = toQualityLevel(previous)

      // Only notify on significant degradation
      if (newLevel === 'poor' && prevLevel !== 'poor' && prevLevel !== 'lost') {
        if (shouldShowToast('local-poor')) {
          toast.warning('Connection quality is poor', {
            description:
              'Your voice may be choppy. Try moving closer to your router.',
            duration: 5000,
          })
        }
      } else if (newLevel === 'lost') {
        if (shouldShowToast('local-lost')) {
          toast.error('Connection lost', {
            description: 'Attempting to reconnect...',
            duration: 6000,
          })
        }
      } else if (
        (newLevel === 'good' || newLevel === 'excellent') &&
        (prevLevel === 'poor' || prevLevel === 'lost')
      ) {
        if (shouldShowToast('local-recovered')) {
          toast.success('Connection restored', {
            description: 'Your connection quality has improved.',
            duration: 3000,
          })
        }
      }
    },
    [shouldShowToast]
  )

  const handleAgentQualityChanged = useCallback(
    (
      quality: ConnectionQuality | null,
      previous: ConnectionQuality | null
    ) => {
      if (!quality) return

      const newLevel = toQualityLevel(quality)
      const prevLevel = previous ? toQualityLevel(previous) : 'unknown'

      if (newLevel === 'poor' && prevLevel !== 'poor' && prevLevel !== 'lost') {
        if (shouldShowToast('agent-poor')) {
          toast.warning('Tutor connection is weak', {
            description: 'You may experience delays in responses.',
            duration: 5000,
          })
        }
      }
    },
    [shouldShowToast]
  )

  const handleReconnecting = useCallback(() => {
    if (shouldShowToast('reconnecting')) {
      toast.loading('Connection interrupted', {
        description: 'Trying to reconnect to your session...',
        id: 'reconnecting-toast',
        duration: Infinity,
      })
    }
  }, [shouldShowToast])

  const handleReconnected = useCallback(() => {
    toast.dismiss('reconnecting-toast')
    toast.success('Reconnected', {
      description: 'Your session has been restored.',
      duration: 3000,
    })
  }, [])

  const handleAgentConnected = useCallback(() => {
    if (shouldShowToast('agent-connected')) {
      toast.success('Tutor joined', {
        description: 'Your AI tutor is ready to help.',
        duration: 3000,
      })
    }
  }, [shouldShowToast])

  const handleAgentDisconnected = useCallback(() => {
    if (shouldShowToast('agent-disconnected')) {
      toast.warning('Tutor disconnected', {
        description:
          'The tutor has left the session. Please wait or try reconnecting.',
        duration: 5000,
      })
    }
  }, [shouldShowToast])

  return {
    handleLocalQualityChanged,
    handleAgentQualityChanged,
    handleReconnecting,
    handleReconnected,
    handleAgentConnected,
    handleAgentDisconnected,
  }
}
